import { gql } from '@apollo/client';
import { css } from '@mildang/styled-system/css';

import { Global, css as emotionCSS } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Theme,
  Typography,
} from '@mildang/mui';
import Button from '@mildang/design-system-legacy/esm/components/molecules/Button';
import TextFieldHook from '@mildang/design-system-legacy/esm/components/molecules/TextField/TextFieldHook';
import { useForm } from 'react-hook-form';
import { debouncePhoneNumber } from '@shared/utils/validate/phoneNumber';
import * as yup from 'yup';
import { FindAccountsByPhoneDocument, FindAccountsByPhoneQuery, FindAccountsByPhoneQueryVariables } from './FindEmailDialog.generated';
import PhoneNumberFormat from './PhoneNumberFormat';
import { globalStyle } from '../ResetPasswordDialog/style';
import { useLazyQuery } from '@apollo/client';


const dialogTitleStyle = css({ paddingTop: '24px', paddingBottom: '0px' });
const formStyle = css({ overflowY: 'auto', display: 'flex', flexDirection: 'column', height: '100%' });
const findAccountBoxStyle = (theme: Theme) => emotionCSS`
  border: 1px solid ${theme.palette.divider};
  border-radius: 4px;
`;
const accountCountStyle = (theme: Theme) => emotionCSS`
  color: ${theme.palette.secondary.dark};
  font-weight: 500;
`;

const emailAndDateListStyle = (theme: Theme) => emotionCSS`
  overflow-y: auto;
  max-height: 25vh;
  padding-top: 16px;
  padding-bottom: 16px;
  ${theme.breakpoints.down('sm')} {
    overflow-x: auto;
  }
`;
const emailAndDateStyle = (theme: Theme) => emotionCSS`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 4px;
  place-content: center;
  padding-top: 4px;
  padding-bottom: 4px;
  ${theme.breakpoints.down('sm')} {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    place-content: flex-start;
  }
`;
const dialogContentStyle = css({ marginBottom: '8px' });
const accountInfoTypoStyle = (theme: Theme) => emotionCSS`
  ${theme.breakpoints.down('sm')} {
    padding-left: 16px;
    padding-right: 16px;
  }
`;
const signupAccountTitleStyle = (theme: Theme) => emotionCSS`
  ${theme.breakpoints.down('sm')} {
    text-align: left;
    padding-left: 16px;
    padding-right: 16px;
  }
`;
const fieldsetStyle = css({ paddingLeft: '24px', paddingRight: '24px', paddingBottom: '16px' });
const dialogContentTextStyle = (theme: Theme) => emotionCSS`
  font-size: ${theme.typography.pxToRem(16)};
  padding-top: 20px;
  margin-bottom: 0px;
`;
const textFieldWrapperStyle = css({ marginTop: '20px' });

const dialogActionsStyle = (theme: Theme) => emotionCSS`
  padding: 16px 24px;
  background-color: ${theme.palette.background.secondary};
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const schema = yup.object().shape({
  name: yup.string().required('이름은 필수값 입니다.'),
  phoneNumber: yup
    .string()
    .required('전화번호는 필수값 입니다.')
    .test('is-phoneNumber', '올바른 핸드폰 번호가 아닙니다.', async (value) => debouncePhoneNumber(value)),
});

interface IValues {
  name: string;
  phoneNumber: string;
}

export interface FindEmailDialogProps extends DialogProps {
  onSubmitProp?: (formData: IValues) => void;
  onSubmitError?: (e: any) => void;
  onCloseClick?: () => void;
}

const FindEmailDialog = (props: FindEmailDialogProps) => {
  const { onSubmitProp, onSubmitError, onCloseClick, ...others } = props;

  const [findAccountsByPhone, { called, loading, data }] = useLazyQuery<FindAccountsByPhoneQuery, FindAccountsByPhoneQueryVariables>(FindAccountsByPhoneDocument);
  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
  } = useForm<IValues>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      phoneNumber: '',
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    onSubmitProp?.(formData);

    try {
      await findAccountsByPhone({
        variables: {
          input: {
            name: formData.name,
            phoneNumber: formData.phoneNumber,
          },
        },
      });
    } catch (e) {
      onSubmitError?.(e);
    }
  });

  const signupAccounts = data?.findAccountsByPhone?.accounts || [];

  return (
    <Dialog
      fullWidth
      aria-labelledby="find-account-dialog-title"
      PaperProps={{
        className: 'LoginScreen_Dialog_Paper',
      }}
      {...others}
    >
      <Global styles={globalStyle} />
      <form className={formStyle} noValidate onSubmit={onSubmit}>
        <DialogTitle className={dialogTitleStyle} id="find-account-dialog-title">
          계정 이메일 찾기
        </DialogTitle>

        <div className={fieldsetStyle}>
          <DialogContentText css={dialogContentTextStyle}>
            가입시 사용한 이름과 연락처를 입력해주세요.
          </DialogContentText>
          <TextFieldHook
            className={textFieldWrapperStyle}
            control={control}
            required
            type="name"
            name="name"
            label="이름"
            placeholder="이름을 입력해주세요"
            fullWidth
          />
          <TextFieldHook
            className={textFieldWrapperStyle}
            control={control}
            required
            name="phoneNumber"
            label="전화번호"
            placeholder="전화번호를 입력해주세요"
            InputProps={{
              inputComponent: PhoneNumberFormat,
            }}
            fullWidth
          />
        </div>
        {called && !loading && (
          <DialogContent className={dialogContentStyle}>
            <div css={findAccountBoxStyle}>
              <div css={emailAndDateListStyle}>
                <Typography
                  css={signupAccountTitleStyle}
                  variant="body2"
                  align="center"
                  color="text.secondary"
                >
                  {signupAccounts.length ? (
                    <>
                      <span>등록된 이메일이 </span>
                      <span css={accountCountStyle}>{signupAccounts.length}</span>
                      <span>개 있어요!</span>
                    </>
                  ) : (
                    <>등록된 이메일이 없어요!</>
                  )}
                </Typography>
                {signupAccounts.map((signupAccount) => (
                  <div key={signupAccount.email} css={emailAndDateStyle}>
                    <Typography css={accountInfoTypoStyle} variant="subtitle2_B">
                      {signupAccount.email}
                    </Typography>
                    <Typography css={accountInfoTypoStyle} variant="body3" color="text.tertiary">
                      가입일:{' '}
                      {new Intl.DateTimeFormat('ko-KR', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric',
                      }).format(new Date(signupAccount.createdAt))}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </DialogContent>
        )}

        <DialogActions css={dialogActionsStyle}>
          <Button disabled={isSubmitting} onClick={() => onCloseClick?.()}>
            취소
          </Button>
          <Button disabled={isSubmitting} variant="contained" color="primary" type="submit">
            계정 찾기
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FindEmailDialog;

gql`
  query FindAccountsByPhone($input: FindAccountsByPhoneInput!) {
    findAccountsByPhone(input: $input) {
      accounts {
        id
        email
        createdAt
        __typename
      }
      __typename
    }
  }
`;
